/**
 * TypologyVoter.ts — CB-D
 *
 * Voter de tipología post-oleada2: infiere InfrastructureType desde el nombre
 * de la infraestructura y/o el tipo declarado por el técnico municipal.
 *
 * Reglas (en orden de prioridad):
 *   R1 — nombre + alias coinciden      → declared_alias,   conf=90
 *   R2 — solo alias válido             → declared_alias,   conf=70
 *   R3 — clasificación automática HIGH → auto_high,        conf=75
 *   R4 — clasificación automática MED  → auto_medium,      conf=55
 *   R5 — tipo declarado sin alias      → declared_only,    conf=40
 *   R6 — sin señal útil               → fallback GENERIC, conf=30
 *        Penalización RED_GENERICA/PLACEHOLDER → conf=0
 *
 * @since CB-D (2026-04-13) — reconstruido por M3 (fichero ausente en merge M4)
 */

import { InfrastructureType } from '../../../types/infrastructure';
import { SIGLA_EDUCATIVA_PUNTEADA } from '../../../lib/siglasEducativas';

// ── Tipos públicos ────────────────────────────────────────────────────────────

export interface VoteInput {
  name: string;
  declaredType?: string | null;
  geocodabilityCategory?: string;
}

export interface AutoClassification {
  type: InfrastructureType;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
}

export interface VoteResult {
  type: InfrastructureType;
  confidence: number;
  source: 'auto+declared' | 'declared_alias' | 'auto_high' | 'auto_medium' | 'declared_only' | 'fallback';
  hasConflict: boolean;
  autoClassification: AutoClassification;
  normalizedDeclaredType: string | null;
}

// ── Tabla de alias PTEL → InfrastructureType ────────────────────────────────

const ALIAS_MAP: Record<string, InfrastructureType> = {
  // Salud
  'sanitario': InfrastructureType.HEALTH,
  'medico':    InfrastructureType.HEALTH,
  'hospital':  InfrastructureType.HEALTH,
  'clinica':   InfrastructureType.HEALTH,
  // Educación
  'educativo': InfrastructureType.EDUCATION,
  'escuela':   InfrastructureType.EDUCATION,
  'colegio':   InfrastructureType.EDUCATION,
  'instituto': InfrastructureType.EDUCATION,
  // Bomberos
  'bomberos':  InfrastructureType.FIRE,
  // Policía
  'policial':  InfrastructureType.POLICE,
  'guardia civil': InfrastructureType.POLICE,
  'policia':   InfrastructureType.POLICE,
  // Hidráulico
  'hidraulico': InfrastructureType.HYDRAULIC,
  'edar':       InfrastructureType.HYDRAULIC,
  'depuradora': InfrastructureType.HYDRAULIC,
  // Emergencias
  'cecopal':    InfrastructureType.EMERGENCY,
  'emergencia': InfrastructureType.EMERGENCY,
  // Maremoto (UPCLS3 cluster eslabón 40 · DERA G12_38 · Decreto 197/2024)
  'maremoto':   InfrastructureType.MAREMOTO,
  'tsunami':    InfrastructureType.MAREMOTO,
  // Religioso
  'religioso':  InfrastructureType.RELIGIOUS,
  'iglesia':    InfrastructureType.RELIGIOUS,
  'parroquia':  InfrastructureType.RELIGIOUS,
  // Telecomunicaciones
  'telecomunicacion': InfrastructureType.TELECOM,
  'antena':           InfrastructureType.TELECOM,
  'repetidor':        InfrastructureType.TELECOM,
};

// ── Corpus de clasificación automática por nombre ────────────────────────────

type CorpusEntry = { type: InfrastructureType; confidence: 'HIGH' | 'MEDIUM' };

const CORPUS: Array<{ pattern: RegExp; type: InfrastructureType; confidence: 'HIGH' | 'MEDIUM' }> = [
  // HEALTH
  { pattern: /hospital|cl[ií]nica|centro de salud|consultorio|ambulatorio/i, type: InfrastructureType.HEALTH,     confidence: 'HIGH' },
  { pattern: /consultorio/i,                                                  type: InfrastructureType.HEALTH,     confidence: 'MEDIUM' },
  // FIRE
  { pattern: /parque de bomberos|bomberos/i,                                  type: InfrastructureType.FIRE,       confidence: 'HIGH' },
  // EDUCATION
  // SIGRE-2: la forma punteada («C.E.I.P. San José», «I.E.S. Acci») no estaba
  // contemplada aquí — no era un `\b` que arreglar, faltaba la alternativa entera.
  // Se interpola SIGLA_EDUCATIVA_PUNTEADA en la MISMA ranura de la alternancia:
  // `autoClassify` devuelve el primer patrón del array que casa, así que sustituir
  // una sub-expresión dentro de la ranura no puede alterar la precedencia entre
  // tipos (HEALTH sigue precediendo a EDUCATION). Mismo molde que ESPI aplicó en
  // services/classification/InfrastructureClassifier.ts L104.
  { pattern: new RegExp(
      `ceip|colegio|instituto|escuela|ies\\b|${SIGLA_EDUCATIVA_PUNTEADA.source}`,
      'i',
    ),                                                                      type: InfrastructureType.EDUCATION,  confidence: 'HIGH' },
  // POLICE
  { pattern: /guardia civil|polic[ií]a|cuartel/i,                            type: InfrastructureType.POLICE,     confidence: 'HIGH' },
  // HYDRAULIC
  { pattern: /edar|depuradora|potabilizadora|abastecimiento/i,               type: InfrastructureType.HYDRAULIC,  confidence: 'HIGH' },
  // EMERGENCY
  { pattern: /cecopal|protecci[oó]n civil/i,                                 type: InfrastructureType.EMERGENCY,  confidence: 'HIGH' },
  // MAREMOTO (UPCLS3 cluster eslabón 40 · DERA G12_38)
  // CRÍTICO: precede a EMERGENCY en orden de declaración para que «punto de encuentro maremoto»
  // matchee aquí antes de que el patrón EMERGENCY genérico (si lo hubiera) lo capture.
  { pattern: /maremoto|tsunami|punto\s+de\s+encuentro\s+(de\s+)?(maremoto|tsunami)/i, type: InfrastructureType.MAREMOTO, confidence: 'HIGH' },
  // RELIGIOUS
  { pattern: /iglesia|parroquia|ermita/i,                                    type: InfrastructureType.RELIGIOUS,  confidence: 'HIGH' },
  // TELECOM
  { pattern: /antena|repetidor|telecomunicaci[oó]n/i,                        type: InfrastructureType.TELECOM,    confidence: 'HIGH' },
  // ENERGY
  { pattern: /subestaci[oó]n|central el[eé]ctrica|transformador/i,           type: InfrastructureType.ENERGY,     confidence: 'HIGH' },
];

const PENALTY_CATEGORIES = new Set(['RED_GENERICA', 'PLACEHOLDER']);

// ── Estado interno (reiniciable para tests) ──────────────────────────────────

let _initialized = true; // sin estado real; reservado para extensión futura

/** @internal Solo para tests unitarios */
export function _resetClassifierForTests(): void {
  _initialized = true;
}

// ── Funciones públicas ────────────────────────────────────────────────────────

/**
 * Normaliza un tipo declarado: minúsculas, sin acentos, recorte de espacios.
 */
export function normalizeDeclaredType(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) return null;
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Resuelve un alias normalizado al InfrastructureType correspondiente.
 * Admite prefijos: "sanitario - cap" → HEALTH.
 */
export function resolveAlias(normalized: string): InfrastructureType | null {
  if (ALIAS_MAP[normalized]) return ALIAS_MAP[normalized];
  // Búsqueda por prefijo
  for (const [alias, type] of Object.entries(ALIAS_MAP)) {
    if (normalized.startsWith(alias)) return type;
  }
  return null;
}

/**
 * Clasifica automáticamente por nombre usando el corpus.
 */
function autoClassify(name: string): AutoClassification {
  for (const entry of CORPUS) {
    if (entry.pattern.test(name)) {
      return { type: entry.type, confidence: entry.confidence };
    }
  }
  return { type: InfrastructureType.GENERIC, confidence: 'NONE' };
}

/**
 * Función principal del voter.
 * Aplica las reglas R1–R6 y devuelve un VoteResult.
 */
export function vote(input: VoteInput): VoteResult {
  const { name, declaredType, geocodabilityCategory } = input;

  const normalizedDeclaredType = normalizeDeclaredType(declaredType);
  const autoClassification = autoClassify(name);
  const autoType = autoClassification.type;
  const autoConf = autoClassification.confidence;

  const aliasType = normalizedDeclaredType ? resolveAlias(normalizedDeclaredType) : null;

  const isPenalized = geocodabilityCategory
    ? PENALTY_CATEGORIES.has(geocodabilityCategory)
    : false;

  // R1: alias + auto coinciden → confirmación mutua
  if (aliasType && autoConf !== 'NONE' && aliasType === autoType) {
    return {
      type: aliasType,
      confidence: isPenalized ? 0 : 85,
      source: 'auto+declared',
      hasConflict: false,
      autoClassification,
      normalizedDeclaredType,
    };
  }

  // R2: solo alias válido (puede haber conflicto con auto)
  if (aliasType) {
    const hasConflict = autoConf !== 'NONE' && autoType !== aliasType;
    return {
      type: aliasType,
      confidence: isPenalized ? 0 : 70,
      source: 'declared_alias',
      hasConflict,
      autoClassification,
      normalizedDeclaredType,
    };
  }

  // R3: clasificación automática HIGH
  if (autoConf === 'HIGH') {
    return {
      type: autoType,
      confidence: isPenalized ? 0 : 75,
      source: 'auto_high',
      hasConflict: false,
      autoClassification,
      normalizedDeclaredType,
    };
  }

  // R4: clasificación automática MEDIUM
  if (autoConf === 'MEDIUM') {
    return {
      type: autoType,
      confidence: isPenalized ? 0 : 55,
      source: 'auto_medium',
      hasConflict: false,
      autoClassification,
      normalizedDeclaredType,
    };
  }

  // R5: tipo declarado sin alias reconocido
  if (normalizedDeclaredType) {
    return {
      type: InfrastructureType.GENERIC,
      confidence: isPenalized ? 0 : 40,
      source: 'declared_only',
      hasConflict: false,
      autoClassification,
      normalizedDeclaredType,
    };
  }

  // R6: sin señal útil
  return {
    type: InfrastructureType.GENERIC,
    confidence: isPenalized ? 0 : 30,
    source: 'fallback',
    hasConflict: false,
    autoClassification,
    normalizedDeclaredType,
  };
}

// ============================================================================
// CB-F: NOMBRE-LOOKUP — voto NGA fuzzy (sobre resultado en memoria)
// ============================================================================

/** Entrada para el voto NGA fuzzy. */
export interface NgaVoteInput {
  /** Nombre buscado (options.name del orchestrator) */
  searchName: string;
  /** matchedName del resultado NGA ya en memoria (Oleada2Candidate.matchedName) */
  ngaMatchedName: string;
  /** Tipo NGA del feature — opcional */
  ngaFeatureType?: string;
}

const NGA_VOTE_WEIGHT = 1.5;
const NGA_FUZZY_THRESHOLD = 75;

/**
 * Extrae un voto de tipología a partir del resultado NGA en memoria.
 * Zero fetches — reutiliza el matchedName de speculativeNgaR2 (oleada2).
 */
export function extractNgaVote(input: NgaVoteInput): {
  type: InfrastructureType;
  confidence: number;
  source: 'nga_fuzzy';
} | null {
  const { searchName, ngaMatchedName, ngaFeatureType: _ngaFeatureType } = input;
  if (!searchName?.trim() || !ngaMatchedName?.trim()) return null;

  const score = computeFuzzyScore(searchName, ngaMatchedName);
  if (score < NGA_FUZZY_THRESHOLD) return null;

  const autoResult = autoClassify(searchName);
  if (autoResult.type === InfrastructureType.GENERIC) return null;

  return {
    type: autoResult.type,
    confidence: Math.min(100, Math.round(score * NGA_VOTE_WEIGHT)),
    source: 'nga_fuzzy',
  };
}

/**
 * Coeficiente de Dice sobre bigramas (0–100).
 * No depende de API privada de uFuzzy.
 */
export function computeFuzzyScore(a: string, b: string): number {
  const na = _normalizeForFuzzy(a);
  const nb = _normalizeForFuzzy(b);
  if (na === nb) return 100;
  if (!na || !nb) return 0;

  const bigramsA = _getBigrams(na);
  const bigramsB = _getBigrams(nb);
  if (bigramsA.length === 0 && bigramsB.length === 0) return 100;
  if (bigramsA.length === 0 || bigramsB.length === 0) return 0;

  let matches = 0;
  const usedB = new Array(bigramsB.length).fill(false);
  for (const bg of bigramsA) {
    const idx = bigramsB.findIndex((b2, i) => !usedB[i] && b2 === bg);
    if (idx !== -1) { matches++; usedB[idx] = true; }
  }
  return Math.round((2 * matches / (bigramsA.length + bigramsB.length)) * 100);
}

/** @internal */
export function _normalizeForFuzzy(s: string): string {
  return s.toLowerCase().normalize('NFD')
    .replace(/\p{Mn}/gu, '').replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ').trim();
}

/** @internal */
export function _getBigrams(s: string): string[] {
  const bigrams: string[] = [];
  for (let i = 0; i < s.length - 1; i++) bigrams.push(s.slice(i, i + 2));
  return bigrams;
}
